import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { accessRequestsAPI } from '../api/accessRequests'

export const useAccessRequests = (filters: {
  status?: string
  role?:   string
  search?: string
  page?:   number
}) => {
  return useQuery({
    queryKey: ['access-requests', filters],
    queryFn:  () => accessRequestsAPI.getAll(filters),
    staleTime: 30_000,
  })
}

// enabled=false empêche l'appel pour les rôles non-admin
export const useAccessRequestStats = (enabled = true) => {
  return useQuery({
    queryKey: ['access-requests-stats'],
    queryFn:  accessRequestsAPI.getStats,
    enabled,
    refetchInterval: enabled ? 60_000 : false,
  })
}

export const useAccessRequestActions = () => {
  const queryClient = useQueryClient()

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    queryClient.invalidateQueries({ queryKey: ['access-requests-stats'] })
  }, [queryClient])

  const approve = useMutation({
    mutationFn: (id: string) => accessRequestsAPI.approve(id),
    onSuccess: invalidate,
  })

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      accessRequestsAPI.reject(id, reason),
    onSuccess: invalidate,
  })

  return { approve, reject }
}

// Hook pour la soumission depuis la landing (gestion d'état locale, pas react-query)
export const useSubmitAccessRequest = () => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  const submit = useCallback(async (payload: Parameters<typeof accessRequestsAPI.submit>[0]) => {
    setLoading(true)
    setError(null)
    try {
      await accessRequestsAPI.submit(payload)
      setSuccess(true)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg ?? 'Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setLoading(false)
    }
  }, [])

  return { submit, loading, success, error }
}
